/**
 * Planes HS — Listado en el portal ESE.
 * El ESE puede crear Planes HS (ej: Fiestas Patrias) con N° OST, gerencia,
 * superintendencia y período. Cada plan auto-crea las 8 acciones mínimas.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { fmtDate } from '../../utils/format';
import { Plus, ShieldCheck, FileText, Calendar, ChevronRight, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EsePlanesHs() {
  const { token, user } = useAuth();
  const isHs = user?.rol === 'equipo_hs' || user?.rol === 'admin' || user?.rol === 'aprobador';
  const rutaBase = isHs ? '/equipo-hs/planes-hs' : '/ese/planes-hs';
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/planes-hs`, { headers });
      setPlanes(r.data);
    } catch (e) {
      toast.error('Error cargando Planes HS');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 grid place-items-center text-white">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Planes HS</h1>
              <p className="text-sm text-slate-500">
                {isHs
                  ? 'Configura Planes HS por empresa colaboradora, define acciones mínimas y supervisa el cumplimiento en tiempo real.'
                  : 'Registra respaldos diarios y visualiza tu % de cumplimiento por cada Plan HS asignado.'}
              </p>
            </div>
          </div>
        </div>
        {isHs && (
          <button
            onClick={() => setShowForm(true)}
            data-testid="btn-nuevo-plan-hs"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow-sm"
          >
            <Plus className="w-4 h-4" /> Nuevo Plan HS
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Cargando...</div>
      ) : planes.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
          <ShieldCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-semibold">
            {isHs ? 'Aún no hay Planes HS creados' : 'Aún no tienes Planes HS asignados'}
          </p>
          <p className="text-sm text-slate-400 mt-1">
            {isHs ? 'Crea el primero para tu empresa colaboradora.' : 'Cuando Equipo HS te asigne un plan aparecerá aquí.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] uppercase text-slate-500 tracking-wide">
                <th className="px-4 py-3 font-semibold">Plan</th>
                {isHs && <th className="px-4 py-3 font-semibold">Empresa</th>}
                <th className="px-4 py-3 font-semibold">N° OST</th>
                <th className="px-4 py-3 font-semibold">Gerencia / Superintendencia</th>
                <th className="px-4 py-3 font-semibold">Periodo</th>
                <th className="px-4 py-3 font-semibold">Cumplimiento</th>
                <th className="px-2 py-3 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {planes.map(p => (
                <PlanRow key={p.id} plan={p} isHs={isHs} onClick={() => navigate(`${rutaBase}/${p.id}`)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm && <NuevoPlanModal headers={headers} isHs={isHs} onClose={() => setShowForm(false)} onCreated={(pid) => { setShowForm(false); load(); navigate(`${rutaBase}/${pid}`); }} />}
    </div>
  );
}

function PlanRow({ plan, isHs, onClick }) {
  const pct = plan.cumplimiento?.porcentaje ?? 0;
  const color = pct >= 90 ? 'emerald' : pct >= 60 ? 'amber' : 'rose';
  return (
    <tr onClick={onClick} data-testid={`plan-row-${plan.id}`} className="hover:bg-slate-50 cursor-pointer">
      <td className="px-4 py-3">
        <p className="font-semibold text-slate-800">{plan.nombre}</p>
        <p className="text-[11px] text-slate-400 mt-0.5 capitalize">{plan.estado}</p>
      </td>
      {isHs && (
        <td className="px-4 py-3 text-slate-600 text-xs truncate max-w-[200px]">
          {plan.empresa_razon_social || plan.empresa?.razon_social || '—'}
        </td>
      )}
      <td className="px-4 py-3 text-slate-600 font-mono text-xs">{plan.numero_contrato_ost}</td>
      <td className="px-4 py-3 text-slate-600 text-xs">
        {plan.gerencia_nombre && <p className="truncate max-w-[220px]">{plan.gerencia_nombre}</p>}
        {plan.superintendencia_nombre && <p className="text-slate-400 truncate max-w-[220px]">{plan.superintendencia_nombre}</p>}
      </td>
      <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">
        <Calendar className="inline w-3 h-3 mr-1 text-slate-400" />
        {fmtDate(plan.periodo_inicio)} → {fmtDate(plan.periodo_fin)}
      </td>
      <td className="px-4 py-3 min-w-[200px]">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden max-w-[140px]">
            <div className={`h-full bg-${color}-500`} style={{ width: `${pct}%` }} />
          </div>
          <span className={`text-xs font-bold text-${color}-600 min-w-[36px] text-right`}>{pct}%</span>
        </div>
        <p className="text-[10px] text-slate-400 mt-0.5">{plan.cumplimiento?.entregados || 0} / {plan.cumplimiento?.requeridos || 0} respaldos</p>
      </td>
      <td className="px-2 py-3"><ChevronRight className="w-4 h-4 text-slate-300" /></td>
    </tr>
  );
}

function NuevoPlanModal({ headers, isHs, onClose, onCreated }) {
  const [gerencias, setGerencias] = useState([]);
  const [supers, setSupers] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [f, setF] = useState({
    nombre: '', numero_contrato_ost: '',
    empresa_colaboradora_id: '',
    gerencia_id: '', gerencia_nombre: '',
    superintendencia_id: '', superintendencia_nombre: '',
    periodo_inicio: '', periodo_fin: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/gerencias`, { headers }).then(r => setGerencias(r.data || [])).catch(() => {});
    if (isHs) {
      axios.get(`${API}/empresas-mandante2`, { headers })
        .then(r => setEmpresas(r.data || []))
        .catch(() => setEmpresas([]));
    }
  }, [headers, isHs]);

  useEffect(() => {
    if (!f.gerencia_id) { setSupers([]); return; }
    axios.get(`${API}/superintendencias/gerencia/${f.gerencia_id}`, { headers })
      .then(r => setSupers(r.data || [])).catch(() => setSupers([]));
  }, [f.gerencia_id, headers]);

  const submit = async () => {
    if (!f.nombre.trim()) { toast.error('Nombre requerido'); return; }
    if (!f.numero_contrato_ost.trim()) { toast.error('N° Contrato/OST requerido'); return; }
    if (isHs && !f.empresa_colaboradora_id) { toast.error('Empresa contratista requerida'); return; }
    if (!f.periodo_inicio || !f.periodo_fin) { toast.error('Periodo requerido'); return; }
    if (new Date(f.periodo_fin) < new Date(f.periodo_inicio)) { toast.error('Fecha fin < inicio'); return; }
    setSaving(true);
    try {
      const payload = { ...f };
      if (!payload.gerencia_id) delete payload.gerencia_id;
      if (!payload.superintendencia_id) delete payload.superintendencia_id;
      if (payload.empresa_colaboradora_id) payload.empresa_colaboradora_id = Number(payload.empresa_colaboradora_id);
      const r = await axios.post(`${API}/planes-hs`, payload, { headers });
      toast.success('Plan HS creado');
      onCreated(r.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al crear');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-slate-800 mb-4">Nuevo Plan HS</h3>
        <div className="space-y-3">
          <Field label="Nombre del Plan *">
            <input value={f.nombre} onChange={e => setF({ ...f, nombre: e.target.value })}
              placeholder="Ej: Plan HS Fiestas Patrias 2026"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="plan-nombre" />
          </Field>
          {isHs && (
            <Field label="Empresa Contratista *">
              <select value={f.empresa_colaboradora_id}
                onChange={e => setF({ ...f, empresa_colaboradora_id: e.target.value })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="plan-empresa">
                <option value="">Seleccionar empresa...</option>
                {empresas.map(e => <option key={e.id} value={e.id}>{e.razon_social}</option>)}
              </select>
            </Field>
          )}
          <Field label="N° Contrato / Orden de Servicio *">
            <input value={f.numero_contrato_ost} onChange={e => setF({ ...f, numero_contrato_ost: e.target.value })}
              placeholder="Ej: OST-2026-001"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="plan-ost" />
          </Field>
          <Field label="Gerencia">
            <select value={f.gerencia_id} onChange={e => {
              const g = gerencias.find(x => x.id === e.target.value);
              setF({ ...f, gerencia_id: e.target.value, gerencia_nombre: g?.nombre || '', superintendencia_id: '', superintendencia_nombre: '' });
            }} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="plan-gerencia">
              <option value="">Seleccionar...</option>
              {gerencias.map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
            </select>
          </Field>
          <Field label="Superintendencia">
            <select value={f.superintendencia_id} onChange={e => {
              const s = supers.find(x => x.id === e.target.value);
              setF({ ...f, superintendencia_id: e.target.value, superintendencia_nombre: s?.nombre || '' });
            }} disabled={!f.gerencia_id} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-50" data-testid="plan-superintendencia">
              <option value="">Seleccionar...</option>
              {supers.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Inicio periodo *">
              <input type="date" value={f.periodo_inicio} onChange={e => setF({ ...f, periodo_inicio: e.target.value })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="plan-fecha-ini" />
            </Field>
            <Field label="Fin periodo *">
              <input type="date" value={f.periodo_fin} onChange={e => setF({ ...f, periodo_fin: e.target.value })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="plan-fecha-fin" />
            </Field>
          </div>
        </div>
        <div className="flex gap-2 mt-6 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm bg-slate-100 hover:bg-slate-200 text-slate-700">Cancelar</button>
          <button onClick={submit} disabled={saving} data-testid="btn-crear-plan"
            className="px-4 py-2 rounded-lg text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold">
            {saving ? 'Creando...' : 'Crear plan'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
